import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { videoConstraints, audioConstraints, DEVICE_KEYS } from "@/lib/media";
import { ArrowLeft, Camera, Mic, Volume2, CheckCircle2, VideoOff } from "lucide-react";

function playChime() {
  const AC = window.AudioContext || window.webkitAudioContext;
  const ctx = new AC();
  [523.25, 659.25, 783.99].forEach((f, i) => {
    const o = ctx.createOscillator(), g = ctx.createGain(), t = ctx.currentTime + i * 0.22;
    o.frequency.value = f; o.type = "sine";
    g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.3, t + 0.03); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
    o.connect(g).connect(ctx.destination); o.start(t); o.stop(t + 0.4);
  });
  setTimeout(() => ctx.close(), 1200);
}

function DevicePicker({ testid, icon: Icon, label, devices, value, onChange }) {
  return (
    <div>
      <p className="text-xs text-emerald-100/60 mb-1.5 flex items-center gap-1.5"><Icon className="h-3.5 w-3.5" /> {label}</p>
      <Select value={value || ""} onValueChange={onChange} disabled={!devices.length}>
        <SelectTrigger data-testid={testid} className="bg-white/5 border-white/10 rounded-xl h-11"><SelectValue placeholder="Default" /></SelectTrigger>
        <SelectContent>
          {devices.map((d, i) => <SelectItem key={d.deviceId} value={d.deviceId} data-testid={`${testid}-option-${i}`}>{d.label || `${label} ${i + 1}`}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function Check({ ok, label, testid }) {
  return (
    <p data-testid={testid} className="flex items-center gap-2 text-sm">
      <CheckCircle2 className={`h-4 w-4 ${ok ? "text-emerald-400" : "text-white/25"}`} /> <span className={ok ? "text-white" : "text-white/50"}>{label}</span>
    </p>
  );
}

export default function SetupTest() {
  const navigate = useNavigate();
  const next = useSearchParams()[0].get("next");
  const videoRef = useRef(null), streamRef = useRef(null), ctxRef = useRef(null), rafRef = useRef(0);
  const [cams, setCams] = useState([]), [mics, setMics] = useState([]);
  const [cam, setCam] = useState(null), [mic, setMic] = useState(null);
  const [level, setLevel] = useState(0), [heard, setHeard] = useState(false), [spoke, setSpoke] = useState(false);
  const [error, setError] = useState(null);

  const stop = () => {
    cancelAnimationFrame(rafRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null;
    ctxRef.current?.close().catch(() => {}); ctxRef.current = null;
  };

  const listDevices = async () => {
    const list = await navigator.mediaDevices.enumerateDevices().catch(() => []);
    setCams(list.filter((d) => d.kind === "videoinput" && d.deviceId)); setMics(list.filter((d) => d.kind === "audioinput" && d.deviceId));
  };

  const open = async (camId, micId) => {
    const v = camId ? { ...videoConstraints(), deviceId: { exact: camId } } : videoConstraints();
    const a = micId ? { ...audioConstraints(), deviceId: { exact: micId } } : audioConstraints();
    try { return await navigator.mediaDevices.getUserMedia({ video: v, audio: a }); }
    catch (e) {
      if (e?.name !== "OverconstrainedError" && e?.name !== "NotFoundError") throw e;
      // Saved/picked device went away (IDs can change between visits) — fall back to defaults
      localStorage.removeItem(DEVICE_KEYS.cam); localStorage.removeItem(DEVICE_KEYS.mic);
      return navigator.mediaDevices.getUserMedia({ video: true, audio: true });
    }
  };

  const start = useCallback(async (camId, micId) => {
    stop();
    try {
      const stream = await open(camId, micId);
      streamRef.current = stream; setError(null);
      if (videoRef.current) videoRef.current.srcObject = stream;
      await listDevices();
      setCam(stream.getVideoTracks()[0]?.getSettings().deviceId || camId); setMic(stream.getAudioTracks()[0]?.getSettings().deviceId || micId);
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC(); ctxRef.current = ctx;
      const an = ctx.createAnalyser(); an.fftSize = 256; ctx.createMediaStreamSource(stream).connect(an);
      const buf = new Uint8Array(an.frequencyBinCount);
      const tick = () => {
        an.getByteFrequencyData(buf);
        const lvl = Math.min(1, buf.reduce((s, x) => s + x, 0) / buf.length / 60);
        setLevel(lvl); if (lvl > 0.25) setSpoke(true);
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (e) {
      setError(e?.name === "NotAllowedError" ? "blocked" : "missing");
      listDevices();
    }
  }, []);

  useEffect(() => { window.scrollTo(0, 0); start(null, null); return stop; }, [start]);
  const bindVideo = (el) => { videoRef.current = el; if (el && streamRef.current && el.srcObject !== streamRef.current) el.srcObject = streamRef.current; };

  const finish = () => {
    if (cam) localStorage.setItem(DEVICE_KEYS.cam, cam);
    if (mic) localStorage.setItem(DEVICE_KEYS.mic, mic);
    localStorage.setItem(DEVICE_KEYS.tested, "1");
    stop(); navigate(next?.startsWith("/call/") ? next : "/profile");
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-10">
      <div data-testid="setup-test-page" className="glass rounded-3xl p-6 sm:p-8 max-w-3xl w-full fade-up">
        <button data-testid="setup-test-back" onClick={() => { stop(); navigate(-1); }} className="text-xs text-emerald-100/60 hover:text-white flex items-center gap-1"><ArrowLeft className="h-3.5 w-3.5" /> Back</button>
        <h1 className="font-display font-black text-3xl sm:text-4xl mt-3">Test your camera & mic</h1>
        <p className="text-sm text-emerald-100/60 mt-1">Nobody else can see or hear this — it's just you.</p>

        <div className="grid sm:grid-cols-2 gap-6 mt-6">
          <div className="relative rounded-2xl overflow-hidden border border-amber-500/20 bg-black aspect-[4/3]">
            {error ? (
              <div data-testid="setup-test-error" className="h-full flex flex-col items-center justify-center text-center p-5 gap-2">
                <VideoOff className="h-7 w-7 text-pink-300" />
                <p className="text-xs text-emerald-100/70">{error === "blocked" ? "Camera & mic are blocked. Tap the lock icon in your browser's address bar, allow both, then try again." : "We couldn't find a camera or mic. Plug one in, then tap Try again."}</p>
                <button data-testid="setup-test-retry" onClick={() => start(null, null)} className="btn-gold rounded-full h-8 px-4 text-xs font-semibold mt-1">Try again</button>
              </div>
            ) : (
              <video data-testid="setup-test-preview" ref={bindVideo} autoPlay playsInline muted className="h-full w-full object-cover scale-x-[-1]" />
            )}
          </div>

          <div className="space-y-4">
            <DevicePicker testid="setup-camera-select" icon={Camera} label="Camera" devices={cams} value={cam} onChange={(id) => start(id, mic)} />
            <DevicePicker testid="setup-mic-select" icon={Mic} label="Microphone" devices={mics} value={mic} onChange={(id) => start(cam, id)} />
            <div>
              <p className="text-xs text-emerald-100/60 mb-1.5">Say something — the bar should move</p>
              <div className="h-2.5 rounded-full bg-white/10 overflow-hidden">
                <div data-testid="setup-mic-level" className="h-full bg-emerald-400 rounded-full transition-[width] duration-100" style={{ width: `${Math.round(level * 100)}%` }} />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button data-testid="setup-speaker-test" onClick={() => playChime()} className="rounded-full h-9 px-4 text-xs font-semibold border border-sky-400/40 text-sky-100 hover:bg-sky-400/10 flex items-center gap-1.5">
                <Volume2 className="h-4 w-4" /> Play test sound
              </button>
              <button data-testid="setup-speaker-heard" onClick={() => setHeard(true)} className={`text-xs ${heard ? "text-emerald-300" : "text-white/60 hover:text-white"}`}>{heard ? "Speaker works ✓" : "I heard it"}</button>
            </div>
            <div className="space-y-1.5 pt-1">
              <Check testid="setup-check-camera" ok={!error && !!streamRef.current} label="Camera working" />
              <Check testid="setup-check-mic" ok={spoke} label="Mic picked you up" />
              <Check testid="setup-check-speaker" ok={heard} label="Speaker working" />
            </div>
            <button data-testid="setup-test-done" onClick={finish} className="w-full btn-gold rounded-full h-12 font-semibold">
              {next ? "Looks good — back to my vibe check" : "Looks good — save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
